import { useValueRef } from '@timelyapp/react';
import { useEffect, useState } from 'react';

export const useDragHandle = (
  onComplete: (delta: number, initialMouseOffsetOnHandle: { x: number; y: number }) => void,
  direction: 'x' | 'y' = 'x'
) => {
  const [handleRef, setHandleRef] = useState<HTMLElement | null>(null);
  const [resizingDelta, setResizingDelta] = useState<number | null>(null);
  const [initialMouseOffsetOnHandle, setInitialMouseOffsetOnHandle] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const onCompleteRef = useValueRef(onComplete);

  useEffect(() => {
    if (!handleRef) {
      return;
    }
    let cleanup = () => {};
    let initialClient = { x: 0, y: 0 };
    let initialOffsetOnHandle = { x: 0, y: 0 };
    const onMouseMove = (e: MouseEvent) => {
      setResizingDelta((direction === 'x' ? e.clientX : e.clientY) - initialClient[direction]);
    };
    const onComplete = (e: MouseEvent) => {
      onCompleteRef.current(
        (direction === 'x' ? e.clientX : e.clientY) - initialClient[direction],
        initialOffsetOnHandle
      );
      done();
    };
    const onMouseLeaveBody = () => {
      done();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        done();
      }
    };
    const done = () => {
      cleanup();
      setResizingDelta(null);
      setInitialMouseOffsetOnHandle(null);
    };
    const setup = (e: MouseEvent) => {
      const isLeftMouseButton = e.button === 0;
      if (e.currentTarget !== handleRef || !isLeftMouseButton) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();

      initialClient = { x: e.clientX, y: e.clientY };
      initialOffsetOnHandle = {
        x: e.clientX - (e.currentTarget as HTMLElement).getBoundingClientRect().x,
        y: e.clientY - (e.currentTarget as HTMLElement).getBoundingClientRect().y,
      };
      setResizingDelta(0);
      setInitialMouseOffsetOnHandle(initialOffsetOnHandle);

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onComplete);
      document.body.addEventListener('mouseleave', onMouseLeaveBody);
      document.body.addEventListener('keydown', onKeyDown);
      cleanup = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onComplete);
        document.body.removeEventListener('mouseleave', onMouseLeaveBody);
        document.body.removeEventListener('keydown', onKeyDown);
      };
    };
    handleRef.addEventListener('mousedown', setup);
    return () => {
      handleRef.removeEventListener('mousedown', setup);
      cleanup();
    };
  }, [handleRef, setResizingDelta, direction, onCompleteRef]);

  return [setHandleRef, resizingDelta, initialMouseOffsetOnHandle] as const;
};
